import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Plus, X, GitBranch, FileZip } from '@phosphor-icons/react';

const ForgeTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [showGit, setShowGit] = useState(false);
  const [showZip, setShowZip] = useState(false);
  const [gform, setGform] = useState({ name: '', git_provider: 'github', repository: '', branch: 'main' });
  const [zform, setZform] = useState({ name: '', file: null });
  const [uploading, setUploading] = useState(false);

  const fetchTemplates = async () => {
    try { const res = await axios.get('/api/forge/templates', { withCredentials: true }); setTemplates(res.data); }
    catch (e) { toast.error('Failed to load templates'); }
  };
  useEffect(() => { fetchTemplates(); }, []);

  const addGit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/forge/templates', { ...gform, type: 'git' }, { withCredentials: true });
      toast.success('Git template saved');
      setShowGit(false); setGform({ name: '', git_provider: 'github', repository: '', branch: 'main' });
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const addZip = async (e) => {
    e.preventDefault();
    if (!zform.file) return toast.error('Choose a .zip file');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('name', zform.name);
      fd.append('file', zform.file);
      await axios.post('/api/forge/templates/upload-zip', fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Zip template uploaded');
      setShowZip(false); setZform({ name: '', file: null });
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await axios.delete(`/api/forge/templates/${id}`, { withCredentials: true }); fetchTemplates(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-black text-[#FB503B]">GIT / ZIP TEMPLATES</h1>
          <p className="text-slate-400 font-mono">Reusable sources for your deployments.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowGit(true)} data-testid="forge-add-git-template" className="flex items-center gap-2 bg-[#FB503B] text-white px-4 py-2 font-bold hover:bg-[#e0402d] transition-colors"><GitBranch size={18} /> Git Repo</button>
          <button onClick={() => setShowZip(true)} data-testid="forge-add-zip-template" className="flex items-center gap-2 bg-[#0A0B0E] border border-white/20 text-white px-4 py-2 font-bold hover:bg-white/10 transition-colors"><FileZip size={18} /> Upload Zip</button>
        </div>
      </div>

      {showGit && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/20 p-8 w-full max-w-lg relative">
            <button onClick={() => setShowGit(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={24} /></button>
            <h2 className="text-2xl font-bold mb-6 text-[#FB503B]">GIT TEMPLATE</h2>
            <form onSubmit={addGit} className="space-y-4">
              <div><label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Name</label>
                <input required value={gform.name} onChange={(e) => setGform({ ...gform, name: e.target.value })} data-testid="forge-template-name" className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#FB503B]" /></div>
              <div><label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Provider</label>
                <select value={gform.git_provider} onChange={(e) => setGform({ ...gform, git_provider: e.target.value })} data-testid="forge-template-provider" className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#FB503B]">
                  <option value="github">GitHub</option><option value="gitlab">GitLab</option><option value="bitbucket">Bitbucket</option><option value="custom">Custom URL</option>
                </select></div>
              <div><label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Repository (owner/repo or full URL)</label>
                <input required value={gform.repository} onChange={(e) => setGform({ ...gform, repository: e.target.value })} data-testid="forge-template-repository" placeholder="acme/laravel-landing" className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 font-mono focus:outline-none focus:border-[#FB503B]" /></div>
              <div><label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Branch</label>
                <input value={gform.branch} onChange={(e) => setGform({ ...gform, branch: e.target.value })} data-testid="forge-template-branch" className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 font-mono focus:outline-none focus:border-[#FB503B]" /></div>
              <button type="submit" data-testid="forge-template-submit" className="w-full bg-[#FB503B] text-white font-bold py-3 hover:bg-[#e0402d] transition-colors">SAVE TEMPLATE</button>
            </form>
          </div>
        </div>
      )}

      {showZip && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/20 p-8 w-full max-w-lg relative">
            <button onClick={() => setShowZip(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={24} /></button>
            <h2 className="text-2xl font-bold mb-2 text-[#FB503B]">ZIP TEMPLATE</h2>
            <p className="text-xs text-slate-500 mb-6 font-mono">Zip contents are uploaded to each site via SSH during deployment.</p>
            <form onSubmit={addZip} className="space-y-4">
              <div><label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Name</label>
                <input required value={zform.name} onChange={(e) => setZform({ ...zform, name: e.target.value })} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#FB503B]" /></div>
              <div><label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Zip File</label>
                <input required type="file" accept=".zip" onChange={(e) => setZform({ ...zform, file: e.target.files[0] })} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 text-sm" /></div>
              <button type="submit" disabled={uploading} className="w-full bg-[#FB503B] text-white font-bold py-3 hover:bg-[#e0402d] transition-colors disabled:opacity-50">{uploading ? 'UPLOADING…' : 'UPLOAD ZIP'}</button>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map(t => (
          <div key={t.id} className="border border-white/10 bg-card p-5 flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {t.type === 'zip' ? <FileZip size={18} className="text-[#FB503B]" /> : <GitBranch size={18} className="text-[#FB503B]" />}
                <h3 className="font-bold">{t.name}</h3>
              </div>
              <p className="text-xs font-mono text-slate-500 break-all">
                {t.type === 'zip' ? `${t.filename} · ${(t.size_bytes / 1024 / 1024).toFixed(2)}MB` : `${t.git_provider}: ${t.repository} @ ${t.branch}`}
              </p>
            </div>
            <button onClick={() => remove(t.id)} data-testid={`forge-template-delete-${t.id}`} className="text-slate-500 hover:text-red-500"><Trash size={16} /></button>
          </div>
        ))}
        {templates.length === 0 && <div className="col-span-full p-8 border border-dashed border-white/20 text-center text-slate-500">No templates yet.</div>}
      </div>
    </div>
  );
};

export default ForgeTemplates;
